import type { RailData } from "@bezbolisno/contracts/read-models";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { AppShell } from "../components/app-shell";
import { NotFoundInShell, RouteErrorInShell } from "../components/boundaries";
import { CommandPalette } from "../components/command-palette";
import { signOut } from "../lib/auth";
import { keys } from "../lib/keys";
import { railQuery } from "../lib/rail";
import { sessionQuery } from "../lib/session";

const EMPTY_RAIL: RailData = { patients: [], patientCount: 0, openTaskCount: 0 };

/**
 * Everything under here needs a staff session (role ≠ none). The session is loaded once
 * per navigation from the query cache; a missing one bounces to /login with the return path.
 * The shell (rail + topbar + ⌘K) wraps every screen; the rail is prefetched by the loader
 * and degrades to an empty list if the read model fails (as the Next layout did).
 */
export const Route = createFileRoute("/_authed")({
  errorComponent: RouteErrorInShell,
  notFoundComponent: NotFoundInShell,
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (!session) throw redirect({ to: "/login", search: { redirect: location.href } });
    if (session.role === "none") throw redirect({ to: "/no-access" });
    return { session };
  },
  loader: ({ context }) => context.queryClient.prefetchQuery(railQuery),
  component: AuthedLayout,
});

function AuthedLayout() {
  const { session, queryClient } = Route.useRouteContext();
  const navigate = useNavigate();
  const { data } = useQuery(railQuery);
  const rail = data ?? EMPTY_RAIL;
  // The shell rail is a working list — archived patients live only in /patients?view=archived.
  const visible = rail.patients.filter((p) => p.status !== "archived");
  const shellRail: RailData = { ...rail, patients: visible, patientCount: visible.length };

  async function logout() {
    await signOut();
    queryClient.setQueryData(keys.session, null);
    queryClient.clear();
    navigate({ to: "/login" });
  }

  return (
    <>
      <AppShell
        rail={shellRail}
        user={{ name: session.name, role: session.role }}
        onLogout={logout}
      >
        <Outlet />
      </AppShell>
      <CommandPalette role={session.role} />
    </>
  );
}
